// Partner hub (Paul, 18 Oct 2026): "a gateway for other things like bid hands together".
//   /partner                       what to do: Weekly tournament or Partnership bidding (then who)
//   /partner?weekly                who to play the weekly tournament with (a partner, or someone new by email)
//   /partner/join/:code            someone's invite link (sign up or sign in, then you're partners)
//   /partner/:pairId/weekly        the weekly tournament, played live as a pair (LiveTable.js)
// Partners need an account each; only one needs to be a member (the weekly tournament checks that).
// All wording here is neutral structural labels for Paul to replace.
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import Signup from "../Auth/Signup";
import { startFacebookLogin, startGoogleLogin, signupEmailAndPasswordLogin, setProfileName } from "../../store/actions/authActions";
import { ph, testUid, rememberCode, takeRememberedCode } from "./phApi";
import LiveTable from "./LiveTable";
import { Avatar, timeAgo } from "./phUi";
import "./PartnerHub.css";

// Page wording (Claude's, at Paul's request 18 Oct 2026: "you come up with wording").
const HUB_INTRO = "Play the weekly tournament with your partner, live at the same table, or bid hands together in your own time.";

const PICK = "pth-partner";

function usePoll(fn, ms) {
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) fn(); }, ms);
    return () => clearInterval(t);
  }, [fn, ms]);
}

// "Someone new" on the weekly tournament screen: invite by email address, straight to the weekly
// tournament (Paul, 18 Oct 2026: email only, no codes). An account holder gets the pop-up; anyone
// without an account is asked to create one first.
function NewPersonWeekly() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const send = async (e) => {
    e.preventDefault();
    const target = email.trim();
    if (!target) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await ph("inviteEmail", { email: target, kind: "play" });
      setMsg(r.live || r.emailed
        ? { ok: true, text: `Weekly tournament invite sent to ${target}. A message pops up here when they answer.` }
        : { ok: false, text: `There's no account for ${target} yet. Ask them to create a free account, then invite them again.` });
      if (r.live) window.dispatchEvent(new Event("pth-inbox-refresh"));
      setEmail("");
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    }
    setBusy(false);
  };
  return (
    <form className="pth-newPerson" onSubmit={send}>
      <label className="pth-label" htmlFor="pth-email">Someone new</label>
      <div className="pth-linkRow">
        <input id="pth-email" className="browser-default pth-input pth-input--big" type="email" placeholder="Their email address" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" autoCapitalize="none" spellCheck={false} />
        <button type="submit" className="pth-btn pth-btn--primary" disabled={busy || !email.trim()}>{busy ? "Sending…" : "Invite to the weekly tournament"}</button>
      </div>
      <p className="pth-note">Your partner needs a Bridge Champions account (a free one is fine). Use the email they signed up with.</p>
      {msg && <p className={msg.ok ? "pth-ok" : "pth-err"} role="status">{msg.text}</p>}
    </form>
  );
}

function Hub({ uid, authReady, location, history }) {
  const as = testUid();
  const signedIn = !!uid || !!as;
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [pick, setPick] = useState(() => {
    try { return new URLSearchParams(location.search).get("with") || localStorage.getItem(PICK); } catch (e) { return null; }
  });
  const [joinMsg, setJoinMsg] = useState("");

  const load = useCallback(() => ph("hub").then((d) => { setData(d); setErr(""); }).catch((e) => setErr(e.message)), []);
  useEffect(() => {
    if (!signedIn) return;
    (async () => {
      // An invite link opened before signing in: become partners now.
      const code = takeRememberedCode();
      if (code) {
        try {
          const r = await ph("join", { code });
          setPick(r.pairId);
          setJoinMsg(`You and ${r.partnerName} are now partners.`);
        } catch (e) {
          setJoinMsg(e.message);
        }
      }
      load();
    })();
  }, [signedIn, load]);
  usePoll(load, 10000);
  // A partner invite accepted from the pop-up: show the new partner straight away.
  useEffect(() => {
    const on = (e) => {
      if (e.detail && e.detail.pairId) {
        setPick(e.detail.pairId);
        try { localStorage.setItem(PICK, e.detail.pairId); } catch (err) { /* storage blocked */ }
      }
      load();
    };
    window.addEventListener("pth-hub-refresh", on);
    return () => window.removeEventListener("pth-hub-refresh", on);
  }, [load]);

  const choose = (pairId) => {
    setPick(pairId);
    try { localStorage.setItem(PICK, pairId); } catch (e) { /* storage blocked */ }
  };
  // Ask a partner to the table now: it pops up on their screen, and you go and sit down.
  const invitePlay = async (p) => {
    choose(p.pairId);
    try {
      await ph("invitePlay", { pairId: p.pairId });
      window.dispatchEvent(new Event("pth-inbox-refresh"));
      history.push(`/partner/${p.pairId}/weekly?invited=1`);
    } catch (e) {
      setErr(e.message);
    }
  };
  const answer = async (inviteId, accept) => {
    try {
      const r = await ph("respond", { inviteId, accept });
      if (r.pairId) choose(r.pairId);
      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  if (!authReady && !as) return <div className="pth-root"><div className="pth-page"><p className="pth-muted">Loading…</p></div></div>;
  if (!signedIn) {
    return (
      <div className="pth-root">
        <Helmet><title>Partners — Bridge Champions</title><meta name="robots" content="noindex" /></Helmet>
        <div className="pth-page pth-narrow">
          <div className="pth-card pth-center">
            <h1 className="pth-h1">Partners</h1>
            <p className="pth-intro">{HUB_INTRO}</p>
            <p className="pth-muted">Sign in to see your partners.</p>
            <div className="pth-actions pth-actions--center">
              <Link className="pth-btn pth-btn--primary" to="/login">Sign in</Link>
              <Link className="pth-btn pth-btn--ghost" to="/signup">Create a free account</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="pth-root"><div className="pth-page">
        {err ? <p className="pth-err" role="alert">{err}</p> : <div className="pth-skeleton" aria-label="Loading" />}
      </div></div>
    );
  }

  const partners = data.partners;
  const weekly = new URLSearchParams(location.search).has("weekly");
  const justJoined = pick && new URLSearchParams(location.search).get("with") ? partners.find((p) => p.pairId === pick) : null;

  const invites = data.invites.map((inv) => (
    <div key={inv.id} className="pth-inviteIn">
      <Avatar name={inv.fromName} size="sm" />
      <div className="pth-grow"><b>{inv.fromName}</b> invited you to be partners.</div>
      <button type="button" className="pth-btn pth-btn--primary pth-btn--sm" onClick={() => answer(inv.id, true)}>Accept</button>
      <button type="button" className="pth-btn pth-btn--quiet pth-btn--sm" onClick={() => answer(inv.id, false)}>Decline</button>
    </div>
  ));

  // Paul, 18 Oct 2026: "Choose what you want to do first, then invite the relevant person."
  // Step 1: what to do. Step 2 (?weekly): who to play the weekly tournament with.
  if (!weekly) {
    return (
      <div className="pth-root">
        <Helmet><title>Partners — Bridge Champions</title><meta name="robots" content="noindex" /></Helmet>
        <div className="pth-page pth-narrow">
          {joinMsg && <p className="pth-banner" role="status">{joinMsg}</p>}
          {justJoined && !joinMsg && <p className="pth-banner" role="status">You and {justJoined.name} are now partners.</p>}
          {err && <p className="pth-err" role="alert">{err}</p>}
          {invites}
          <h1 className="pth-h1">Partners</h1>
          <p className="pth-muted pth-lead">What would you like to do?</p>
          <div className="pth-acts">
            <Link className="pth-act pth-act--weekly" to="/partner?weekly">
              <span className="pth-ic pth-ic--green" aria-hidden="true">🏆</span>
              <span className="pth-actTxt"><b>Weekly tournament</b><span>Live, at the same table</span></span>
              <span className="pth-actArrow" aria-hidden="true">→</span>
            </Link>
            <Link className="pth-act" to="/partner-bidding">
              <span className="pth-ic pth-ic--blue" aria-hidden="true">💬</span>
              <span className="pth-actTxt"><b>Partnership bidding</b><span>Bid hands in your own time</span></span>
              <span className="pth-actArrow" aria-hidden="true">→</span>
            </Link>
            {/* Pairs events (Paul, 31 Oct 2026) - hidden: admins (and config/pairsEvents.allow) only, for now. */}
            {data.me.events && <Link className="pth-act" to="/events">
              <span className="pth-ic pth-ic--green" aria-hidden="true">🃏</span>
              <span className="pth-actTxt"><b>Events</b><span>Pairs v pairs, live, at a set time</span></span>
              <span className="pth-actArrow" aria-hidden="true">→</span>
            </Link>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pth-root">
      <Helmet><title>Weekly tournament with a partner — Bridge Champions</title><meta name="robots" content="noindex" /></Helmet>
      <div className="pth-page pth-narrow">
        {err && <p className="pth-err" role="alert">{err}</p>}
        {invites}
        <Link className="pth-backText" to="/partner">← Partners</Link>
        <h1 className="pth-h1">Weekly tournament</h1>
        <p className="pth-muted pth-lead">Who do you want to play with?</p>
        <p className="pth-note pth-note--rule">At least one of you needs to be a member to play the weekly tournament.</p>
        <section className="pth-card">
          {partners.length > 0 && (
            <ul className="pth-plist">
              {partners.map((p) => (
                <li key={p.pairId}>
                  <Avatar name={p.name} online={p.online} size="sm" />
                  <span className="pth-pname pth-pname--static">
                    {p.name}
                    <small>
                      {p.weekly.partnerAtTable ? "At the weekly tournament table" : p.online ? "Online now" : p.lastSeen ? `Last seen ${timeAgo(p.lastSeen)}` : "Not seen yet"}
                      {p.weekly.played ? ` · ${p.weekly.played} of ${data.boardCount} boards played` : ""}
                    </small>
                  </span>
                  {p.weekly.partnerAtTable ? (
                    <Link className="pth-btn pth-btn--sm pth-btn--primary" to={`/partner/${p.pairId}/weekly`} onClick={() => choose(p.pairId)}>Join {p.name} at the weekly tournament</Link>
                  ) : (
                    <button type="button" className={`pth-btn pth-btn--sm ${p.online ? "pth-btn--primary" : "pth-btn--ghost"}`} onClick={() => invitePlay(p)}>Invite to the weekly tournament</button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <NewPersonWeekly />
        </section>
      </div>
    </div>
  );
}

function JoinPage({ uid, authReady, match, history, startFacebookLogin, startGoogleLogin, signupEmailAndPasswordLogin, setProfileName }) {
  const code = String(match.params.code || "").toUpperCase();
  const signedIn = !!uid || !!testUid();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!authReady && !testUid()) return;
    ph("peek", { code }, { anon: true }).then(setInfo).catch((e) => setErr(e.message));
  }, [code, authReady, uid]);
  useEffect(() => {
    if (!signedIn) rememberCode(code);
  }, [signedIn, code]);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await ph("join", { code });
      takeRememberedCode();
      history.push(`/partner?with=${encodeURIComponent(r.pairId)}`);
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  const from = (info && info.fromName) || "";
  return (
    <div className="pth-root">
      <Helmet><title>Partner invite — Bridge Champions</title><meta name="robots" content="noindex" /></Helmet>
      <div className="pth-page pth-narrow">
        <div className="pth-card pth-center pth-joinCard">
          <div className="pth-pairAv pth-pairAv--center">
            <Avatar name={from || "?"} online />
            <span className="pth-av pth-av--lg pth-av--empty pth-av--overlap" aria-hidden="true">{signedIn ? "✓" : "?"}</span>
          </div>
          {err ? (
            <>
              <p className="pth-err" role="alert">{err}</p>
              <Link className="pth-btn pth-btn--ghost" to="/partner">Go to your partners</Link>
            </>
          ) : !info ? (
            <p className="pth-muted">Loading…</p>
          ) : info.self ? (
            <>
              <h1 className="pth-h1">This is your own invite</h1>
              <p className="pth-muted">Send this link to your partner.</p>
              <Link className="pth-btn pth-btn--primary" to="/partner">Go to your partners</Link>
            </>
          ) : (
            <>
              <h1 className="pth-h1">{from} has invited you to be partners</h1>
              <ol className="pth-steps">
                <li className="is-done">Open the invite</li>
                <li className={signedIn ? "is-done" : ""}>Sign in or create a free account</li>
                <li>Partners</li>
              </ol>
              {signedIn ? (
                <button type="button" className="pth-btn pth-btn--primary pth-btn--wide" onClick={accept} disabled={busy}>
                  {info.already ? "Go to your partners" : `Become partners with ${from}`}
                </button>
              ) : (
                <div className="pth-signup">
                  <Signup
                    embedded
                    embeddedTitle="Create a free account"
                    embeddedSubtitle=" "
                    facebookLogin={startFacebookLogin}
                    googleLogin={startGoogleLogin}
                    emailLogin={signupEmailAndPasswordLogin}
                    setProfileName={setProfileName}
                    history={history}
                    redirectPathAfterAuth={`/partner/join/${code}`}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PartnerHub(props) {
  const { match } = props;
  if (match.params.code) return <JoinPage {...props} />;
  if (match.params.pairId) return <LiveTable pairId={match.params.pairId} uid={props.uid} authReady={props.authReady} />;
  return <Hub {...props} />;
}

export default withRouter(connect(
  (state) => ({ uid: state.auth?.uid, authReady: state.auth?.authReady === true }),
  (dispatch) => ({
    startFacebookLogin: () => dispatch(startFacebookLogin()),
    startGoogleLogin: () => dispatch(startGoogleLogin()),
    signupEmailAndPasswordLogin: (email, password) => dispatch(signupEmailAndPasswordLogin(email, password)),
    setProfileName: (firstName, surname) => dispatch(setProfileName(firstName, surname)),
  })
)(PartnerHub));
